import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { CandidatePoolFiltersSection } from "@/components/guild-war/candidate-pool-filters-section"
import { CandidatePoolManualAddSection } from "@/components/guild-war/candidate-pool-manual-add-section"
import { CandidatePoolResultsSection } from "@/components/guild-war/candidate-pool-results-section"
import { RefreshButton } from "@/components/shared/refresh-button"
import { StatusAlert } from "@/components/shared/status-alert"
import { formatWeekendRange } from "@/core/guild-war/weekend-dates"
import { guildWarApi } from "@/lib/guild-war-api"
import { selectCoreCandidatePool } from "@/lib/guild-war-query-transforms"
import { guildWarKeys } from "@/lib/query-keys"
import { createCandidatePoolUiStore } from "@/lib/stores/candidate-pool-ui-store"

export const Route = createFileRoute(
  "/_authed/_admin/guild-war/candidate-pool",
)({
  beforeLoad: () => ({
    candidatePoolStore: createCandidatePoolUiStore(),
  }),
  component: CandidatePoolPage,
})

export function CandidatePoolPage() {
  const { candidatePoolStore } = Route.useRouteContext()

  useEffect(() => {
    candidatePoolStore.setState({
      candidateSearch: "",
      debouncedCandidateSearch: "",
      page: 1,
    })
  }, [candidatePoolStore])

  const weeksQuery = useQuery({
    queryKey: guildWarKeys.weeks(),
    queryFn: guildWarApi.listWeeks,
  })

  const filters = useStore(candidatePoolStore, (s) => s.filters)
  const effectiveWeekId = filters.weekId || weeksQuery.data?.[0]?.id || ""

  const weekOptions = useMemo(
    () =>
      (weeksQuery.data ?? []).map((week) => ({
        value: week.id,
        label: `${formatWeekendRange({ weekStartAt: week.weekStartAt })} (${week.state})`,
      })),
    [weeksQuery.data],
  )

  const candidateQuery = useQuery({
    queryKey: [...guildWarKeys.candidatePool(effectiveWeekId), "client-all"],
    queryFn: () =>
      guildWarApi.getCandidatePool({
        weekId: effectiveWeekId,
        sortBy: "member",
        sortOrder: "asc",
        page: 1,
        pageSize: 100,
      }),
    enabled: Boolean(effectiveWeekId),
    select: selectCoreCandidatePool,
  })

  const handleRefresh = () => {
    void weeksQuery.refetch()
    if (effectiveWeekId) {
      void candidateQuery.refetch()
    }
  }

  const isFetching = weeksQuery.isFetching || candidateQuery.isFetching

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Candidate Pool</h2>
        <RefreshButton isFetching={isFetching} onClick={handleRefresh} />
      </div>
      <CandidatePoolManualAddSection effectiveWeekId={effectiveWeekId} />
      {weeksQuery.isError ? (
        <StatusAlert tone="danger">
          Failed to load weeks.
          <Button
            variant="ghost"
            size="sm"
            className="ml-2"
            onClick={() => void weeksQuery.refetch()}
          >
            Retry
          </Button>
        </StatusAlert>
      ) : null}
      <CandidatePoolFiltersSection
        weekOptions={weekOptions}
        effectiveWeekId={effectiveWeekId}
      />
      <CandidatePoolResultsSection
        rawItems={candidateQuery.data?.items ?? []}
        hasData={Boolean(candidateQuery.data)}
        isLoading={candidateQuery.isLoading}
        isError={candidateQuery.isError}
        onRefetch={() => void candidateQuery.refetch()}
      />
    </div>
  )
}
